'use client';

import {
	BigNum,
	PRICE_PRECISION_EXP,
	PositionDirection,
	QUOTE_PRECISION_EXP,
	ZERO,
} from '@drift-labs/sdk';
import MiniChart from 'src/components/MiniChart';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import CentreFillScroller from 'src/components/Utils/CentreFillScroller';
import MarketIcon from 'src/components/Utils/MarketIcon';
import NumberDisplay, {
	NumberDisplayProps,
} from 'src/components/NumberDisplay/NumberDisplay';
import useIsMobile from 'src/hooks/useIsMobileScreenSize';
import React, {
	CSSProperties,
	MouseEvent,
	PropsWithChildren,
	ReactNode,
	useMemo,
	useState,
} from 'react';
import { format } from 'timeago.js';
import { isNotionalDust } from 'src/utils/validation';
import {
	getDelistingTextOpenPosition,
	isDelistingTimestampInFuture,
	marketToBeDelisted,
} from 'src/utils/delisting';
import { baseSizeDisplayForPerpMarket } from 'src/utils/position';
import { cj, cm } from 'src/utils/classNames';
import { MAIN_POOL_ID, matchEnum, OpenPosition, UIMarket } from '@drift/common';
import Chevron from '../Icons/Chevron';
import useInterval from 'src/hooks/useInterval';
import SortIcon from '../Icons/SortIcon';
import { Hotified, Info, WarningFilled } from '@drift-labs/icons';
import { PredictionMarketConfigs } from 'src/hooks/predictionMarkets/predictionMarketConfigs';
import { PageRoute, POOL_NAMES_BY_POOL_ID } from 'src/constants/constants';
import { JSX } from 'react';
import { AccountOpenPosition } from 'src/@types/positions';
import { getIsZeroFeesActiveForMarket } from 'src/utils/zeroFees';
import { useSwitchMarket } from 'src/hooks/driftActions/market/useSwitchMarket';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import dayjs from 'dayjs';
import { WithChildren, WithClassName, WithRef } from 'src/utils/UtilityTypes';
import { getColorVar } from 'src/utils/theme/colorVars';
import Button from 'src/components/primitives/Button/Button';
import { STANDARD_DATE_FORMAT } from 'src/constants/dates';
import VariantTag from 'src/components/Tags/VariantTag';
import Tag from 'src/components/primitives/Tag/Tag';

const Skeleton = (
	props: PropsWithChildren<{
		top: React.ReactNode;
		middle: React.ReactNode;
		bottom?: React.ReactNode;
		hideHeadersOnMobile?: boolean;
		hideFooterOnMobile?: boolean;
		noBorder?: boolean;
		noBg?: boolean;
		notRounded?: boolean;
		fillSpace?: boolean;
		className?: string;
		autoHeight?: boolean;
		innerClassName?: string;
		setIsScrollable?: (isScrollable: boolean) => void;
	}>
) => {
	const isMobile = useIsMobile();

	return (
		<CentreFillScroller
			outerClassName={cm(
				'overflow-hidden text-text-default',
				props.noBorder ? 'border-0' : 'border border-stroke-divider',
				props.className
			)}
			top={props.hideHeadersOnMobile && isMobile ? null : props.top}
			middle={<BodyRowWrapper noBg>{props.middle}</BodyRowWrapper>}
			bottom={props.hideFooterOnMobile && isMobile ? null : props.bottom}
			fillSpace={props.fillSpace}
			autoHeight={props.autoHeight}
			innerClassName={props.innerClassName}
			overflowAuto
			setIsScrollable={props.setIsScrollable}
		/>
	);
};

// -- ROWS

interface TableRowWrapperProps extends WithChildren, WithClassName {
	id?: string;
	grid: string;
	lastColumnJustify?: 'start' | 'end';
	onClick?: (e: MouseEvent<HTMLDivElement>) => void;
}

function TableRowWrapper({
	grid,
	lastColumnJustify,
	className,
	...rest
}: WithRef<TableRowWrapperProps, HTMLDivElement>) {
	return (
		<div
			className={cm(
				'grid grid-flow-row auto-rows-auto border-stroke-divider bg-container-bg',
				[
					'[&>*]:items-left [&>*]:justify-self-start [&>*]:text-left [&>*]:first-of-type:pl-4',
					'[&>*]:last:items-left [&>*]:last:pr-4 [&>*]:last:text-left',
					lastColumnJustify === 'start'
						? '[&>*]:last:justify-self-start'
						: '[&>*]:last:justify-self-end',
				],
				className
			)}
			style={{ gridTemplateColumns: grid }}
			{...rest}
		/>
	);
}

interface HeaderRowProps extends Pick<
	TableRowWrapperProps,
	'id' | 'children' | 'className' | 'lastColumnJustify' | 'grid'
> {
	/**
	 * Adds padding to the right of the table to account for scrollbar in the table.
	 */
	addScrollPadding?: boolean;
}

const HeaderRow = ({
	className,
	addScrollPadding,
	...rest
}: WithRef<HeaderRowProps, HTMLDivElement>) => {
	return (
		<TableRowWrapper
			className={cm(
				'border-b bg-container-bg py-sm',
				addScrollPadding && 'pr-2',
				className
			)}
			{...rest}
		/>
	);
};

const BodyRowWrapper = ({
	children,
	noBg,
}: WithChildren<{ noBg?: boolean }>) => {
	return (
		<div
			className={cj('flex h-full w-full flex-col', !noBg && 'bg-container-bg')}
		>
			{children}
		</div>
	);
};

interface BodyRowProps extends TableRowWrapperProps {
	isDataRow?: boolean;
}

const BodyRow = ({ className, isDataRow, ...rest }: BodyRowProps) => {
	return (
		<TableRowWrapper
			className={cm(
				'border-none bg-container-bg hover:bg-button-disabled',
				className
			)}
			data-puppet-tag={isDataRow ? 'table_data_row' : undefined}
			{...rest}
		/>
	);
};

type SummaryRowProps = Omit<TableRowWrapperProps, 'noBorder' | 'topBorder'>;

const SummaryRow = ({ className, ...rest }: SummaryRowProps) => {
	return (
		<TableRowWrapper
			className={cm('border-t bg-container-bg', className)}
			{...rest}
		/>
	);
};

// -- CELLS

const HeaderCellWrapper = ({
	children,
	className,
	alignRight,
}: PropsWithChildren<{
	className?: string;
	alignRight?: boolean;
}>) => {
	return (
		<div
			className={cm(
				'flex w-full items-center typo-b4 capitalize select-none',
				'bg-container-bg typo-b4 text-text-secondary',
				alignRight && 'justify-end',
				className
			)}
		>
			{children}
		</div>
	);
};

const HeaderCell = ({
	children,
	className,
	alignRight,
}: PropsWithChildren<{
	className?: string;
	alignRight?: boolean;
}>) => {
	return (
		<HeaderCellWrapper className={className} alignRight={alignRight}>
			{children}
		</HeaderCellWrapper>
	);
};

const TooltipHeaderCell = ({
	children,
	className,
}: PropsWithChildren<{
	className?: string;
}>) => {
	return (
		<HeaderCellWrapper className={cm('gap-x-sm', className)}>
			{children}
		</HeaderCellWrapper>
	);
};

const SortableHeaderCell = ({
	children,
	className,
	alignRight,
	sortDirection,
	onChange,
	wrapperClassName,
}: PropsWithChildren<{
	sortDirection: 'high' | 'low' | 'none';
	onChange: () => void;
	className?: string;
	alignRight?: boolean;
	wrapperClassName?: string;
}>) => {
	return (
		<HeaderCellWrapper
			className={cm('gap-x-sm', className)}
			alignRight={alignRight}
		>
			<div
				className={cm(
					'relative flex h-5 cursor-pointer flex-row items-center justify-start pr-7 select-none',
					wrapperClassName
				)}
				onClick={onChange}
			>
				<div className="max-w-fit shrink">{children}</div>
				<SortIcon
					onClick={onChange}
					state={
						sortDirection === 'low'
							? 'ascending'
							: sortDirection === 'high'
								? 'descending'
								: 'neutral'
					}
					size={20}
				/>
			</div>
		</HeaderCellWrapper>
	);
};

const BodyCellWrapper = ({
	children,
	className,
	label,
	onClick,
	dataPuppetTag,
	flexCol,
	alignCenter,
	alignRight,
	innerClassName,
}: PropsWithChildren<{
	className?: string;
	innerClassName?: string;
	label?: string;
	onClick?: (e: MouseEvent<HTMLDivElement>) => void;
	dataPuppetTag?: string;
	flexCol?: boolean;
	alignCenter?: boolean;
	alignRight?: boolean;
}>) => {
	return (
		<div
			className={cm(
				'flex w-full flex-row items-center py-2',
				alignRight && 'justify-end',
				onClick && 'hover:cursor-pointer',
				className
			)}
			onClick={onClick}
			data-puppet-tag={dataPuppetTag}
		>
			{label && <div className="text-2xs text-text-secondary">{label}</div>}
			<span
				className={cm(
					'flex typo-t4',
					alignCenter
						? 'items-center'
						: alignRight
							? 'items-end'
							: 'items-start',
					flexCol ? 'flex-col' : 'flex-row',
					innerClassName
				)}
			>
				{children}
			</span>
		</div>
	);
};

const BodyCell = ({
	children,
	className,
	onClick,
	dataPuppetTag,
	flexCol,
	alignCenter,
	alignRight,
	innerClassName,
}: PropsWithChildren<{
	className?: string;
	innerClassName?: string;
	onClick?: (e: MouseEvent<HTMLDivElement>) => void;
	dataPuppetTag?: string;
	flexCol?: boolean;
	alignCenter?: boolean;
	alignRight?: boolean;
}>) => {
	return (
		<BodyCellWrapper
			className={className}
			onClick={onClick}
			dataPuppetTag={dataPuppetTag}
			flexCol={flexCol}
			alignCenter={alignCenter}
			alignRight={alignRight}
			innerClassName={innerClassName}
		>
			{children}
		</BodyCellWrapper>
	);
};

interface CommonTableItemProps extends PropsWithChildren {
	className?: string;
	marketSymbol: string;
	/**
	 * Icons rendered within the PnL modal are fetched from our `api/market-icon` route.
	 *
	 * In order to take advantage of caching, tables from which the PnL modal can be opened
	 * (e.g. positions table) should also fetch their market icons from the Next API route.
	 */
	useNextApiRouteForMarketIcon?: boolean;
}

export interface MarketCellProps extends CommonTableItemProps {
	innerClassName?: string;
	large?: boolean;
	onClick?: (e: MouseEvent<HTMLDivElement>) => void;
	isSpot?: boolean;
	direction?: 'short' | 'long' | 'buy' | 'sell';
	expandable?: {
		isParent: boolean;
		expanded?: boolean;
	};
	textClass?: string;
	suffix?: string | ReactNode;
	isNew?: boolean;
	isHot?: boolean;
	highlight?: boolean;
	baseAssetSymbol?: string;
	poolId?: keyof typeof POOL_NAMES_BY_POOL_ID;
	customHeight?: number;
	customWidth?: number;
	isSignedMsgTrade?: boolean;
	showPromoChips?: boolean;
	marketIndex?: number;
	reduceOnlyPerpMarket?: boolean;
	maxWidth?: number;
}

const MarketCell = (props: MarketCellProps) => {
	const isPredictionMarket = PredictionMarketConfigs.isPredictionMarket(
		props.marketSymbol
	);
	const predictionMarketIndex =
		PredictionMarketConfigs.getPredictionMarketIndex(props.marketSymbol);

	const predictionMarketTitle = isPredictionMarket
		? (PredictionMarketConfigs.get(predictionMarketIndex ?? 0)?.shortTitle ??
			'')
		: null;

	const poolIdName = POOL_NAMES_BY_POOL_ID[props.poolId ?? MAIN_POOL_ID];

	const showZeroFeesPromoChip = (() => {
		if (props.marketIndex === undefined || !props.showPromoChips) {
			return false;
		}

		return getIsZeroFeesActiveForMarket({
			marketIndex: props.marketIndex,
			isPerp: !props.isSpot,
		});
	})();

	return (
		<BodyCell
			className={props.className}
			onClick={props.onClick}
			alignCenter
			innerClassName={props.innerClassName}
		>
			{props.expandable?.isParent && (
				<div className="mr-2 flex w-lg items-center justify-center duration-300">
					<Chevron direction={props.expandable.expanded ? 'down' : 'right'} />
				</div>
			)}
			<MarketIcon
				marketSymbol={props.marketSymbol}
				className={'mr-2'}
				customHeight={props.customHeight}
				customWidth={props.customWidth}
				isSpot={props.isSpot}
				useNextApiRoute={props.useNextApiRouteForMarketIcon}
			/>
			<div className="flex flex-col items-start justify-center">
				<div className="flex items-center justify-center">
					{isPredictionMarket ? (
						<div
							className={cm(
								'normal-case xl:block',
								!props.baseAssetSymbol && 'hidden'
							)}
						>
							<Tooltip content={`Prediction Market: ${predictionMarketTitle}`}>
								{
									<span className="line-clamp-2 overflow-hidden text-ellipsis">
										{predictionMarketTitle}
									</span>
								}
							</Tooltip>
						</div>
					) : (
						<span
							className={cm('mt-0.5 truncate', props.textClass)}
							style={{
								maxWidth: props.maxWidth ? `${props.maxWidth}px` : `120px`,
							}}
						>
							{props.marketSymbol} {props.suffix}
						</span>
					)}
					{props.isHot && (
						<Hotified size={18} color={getColorVar('negative-red')} />
					)}
				</div>
				{!!props.poolId && !!poolIdName && (
					<div className="text-text-secondary">{poolIdName}</div>
				)}
				<div className="flex items-center gap-x-xs">
					{props.direction && (
						<span
							className={cj(
								'capitalize',
								props.direction.match(/buy|long/)
									? 'bg-positive-green-secondary-bg px-1 py-px text-text-positive-green-button'
									: 'bg-negative-red-secondary-bg px-1 py-px text-text-negative-red-button'
							)}
						>
							{props.direction}
						</span>
					)}
					{props.isNew && <VariantTag variant="new" />}
					{showZeroFeesPromoChip && props.marketIndex !== undefined && (
						<VariantTag variant="zero-fees" />
					)}
				</div>
			</div>
			{isDelistingTimestampInFuture() &&
				marketToBeDelisted(props.marketIndex) &&
				props.marketIndex &&
				props.reduceOnlyPerpMarket && (
					<Tooltip content={getDelistingTextOpenPosition()}>
						<WarningFilled
							size={18}
							className="ml-2"
							color={getColorVar('star-yellow')}
						/>
					</Tooltip>
				)}
		</BodyCell>
	);
};

interface NotionalCellProps extends Omit<NumberDisplayProps, 'type'> {
	semantic?: boolean;
	value: BigNum;
	label?: string;
	disableTooltip?: boolean;
}

const NotionalCell = (props: NotionalCellProps) => {
	return (
		<BodyCell {...props} className={`${props.className}`}>
			<Tooltip
				decoration="cursor-help"
				content={props.value.toNotional()}
				disabled={props.disableTooltip}
			>
				<NumberDisplay
					className="pt-0.5"
					type={props.semantic ? 'currency_signed' : 'currency'}
					value={props.value}
					signDependentColor={props.signDependentColor}
					displayOverride={
						props.displayOverride
							? props.displayOverride
							: !props.decimals && isNotionalDust(props.value)
								? '< $0.01'
								: undefined
					}
					isAccountValueToHide={props.isAccountValueToHide}
					decimals={props.decimals ?? 2}
					dimIfZero={props.dimIfZero}
					millify={props.millify}
					trimZeroes={props.trimZeroes}
				/>
			</Tooltip>
		</BodyCell>
	);
};

const TradeSizeCell = (
	props: PropsWithChildren<{
		baseSize: BigNum;
		baseSymbol: string;
		marketSymbol: string;
		hideValues: boolean;
		className?: string;
	}>
) => {
	const isPredictionMarket = PredictionMarketConfigs.isPredictionMarket(
		props.marketSymbol
	);

	return (
		<BodyCell {...props} className={`flex ${props.className}`}>
			<div className="align-left flex flex-col">
				{isPredictionMarket ? (
					baseSizeDisplayForPerpMarket(
						props.baseSize,
						props.marketSymbol,
						props.hideValues
					)
				) : (
					<NumberDisplay
						value={props.baseSize}
						type="number"
						suffix={props.baseSymbol}
						trimZeroes
					/>
				)}
			</div>
		</BodyCell>
	);
};

interface AssetCellProps extends Omit<NumberDisplayProps, 'type'> {
	semantic?: boolean;
	value: BigNum;
	label?: string;
	notionalValue?: BigNum;
	textClassname?: string;
	disableTooltip?: boolean;
}

const AssetCell = (props: AssetCellProps) => {
	const decimals = props.decimals;
	const precision = props.precision ?? 'trade';

	return (
		<BodyCell {...props} className={`flex ${props.className}`}>
			<div className="align-left flex flex-col">
				<Tooltip
					content={
						<span>
							{props.value.prettyPrint()} {props.suffix}
						</span>
					}
					disabled={props.disableTooltip}
				>
					<NumberDisplay
						value={props.value}
						type={props.semantic ? 'number_signed' : 'number'}
						suffix={props.suffix}
						precision={decimals ? undefined : precision}
						decimals={decimals}
						signDependentColor={props.semantic}
						isAccountValueToHide={props.isAccountValueToHide}
						className={cm('pt-0.5', props.textClassname)}
						trimZeroes={props.trimZeroes}
					/>
				</Tooltip>
				{props.notionalValue && (
					<NumberDisplay
						value={props.notionalValue}
						type={props.semantic ? 'currency_signed' : 'currency'}
						decimals={2}
						className="pt-0.5 text-text-secondary"
						isAccountValueToHide={props.isAccountValueToHide}
						trimZeroes={props.trimZeroes}
					/>
				)}
			</div>
		</BodyCell>
	);
};

const DateCell = (
	props: PropsWithChildren<{
		className?: string;
		innerClassName?: string;
		date: Date;
		displayAsTimestamp?: boolean;
		label?: string;
		hideTime?: boolean;
	}>
) => {
	const [timeAgoString, setTimeAgoString] = useState(format(props.date));

	// update timeAgo string every 10 seconds
	useInterval(() => {
		setTimeAgoString(format(props.date));
	}, 10000);

	return (
		<BodyCell className={props.className} innerClassName={props.innerClassName}>
			{props.displayAsTimestamp ? (
				props.hideTime ? (
					<>{dayjs(props.date).format(STANDARD_DATE_FORMAT)}</>
				) : (
					<>{dayjs(props.date).format(`${STANDARD_DATE_FORMAT} h:mm:ss A`)}</>
				)
			) : (
				<Tooltip
					decoration="none"
					content={dayjs(props.date).format(
						`${STANDARD_DATE_FORMAT} h:mm:ss A`
					)}
				>
					{timeAgoString}
				</Tooltip>
			)}
		</BodyCell>
	);
};

const ChartCell = (
	props: PropsWithChildren<{
		className?: string;
		chartData: Parameters<typeof MiniChart>[0]['data'];
		useMinMaxTicks?: boolean;
		forcePositive?: boolean;
	}>
) => {
	return (
		<BodyCell className={props.className}>
			<MiniChart
				data={props.chartData}
				useMinMaxTicks={props.useMinMaxTicks}
				isPositive={
					props.forcePositive ??
					(props.chartData?.at(-1) ?? 0) >= (props.chartData?.[0] ?? 0)
				}
			/>
		</BodyCell>
	);
};

const ActionCell = (
	props: PropsWithChildren<{
		className?: string;
	}>
) => {
	return (
		<BodyCell className={`flex space-x-2 ${props.className}`}>
			{props.children}
		</BodyCell>
	);
};

const OpenPositionMarketDirectionCell = (props: {
	position: AccountOpenPosition;
	handleClickMarket?: () => void;
}) => {
	// TODO: Surely something better possible than this?
	const isPerp = props.position.marketSymbol.match('-PERP'); // Not the best, can fix later though

	// TODO: Can probably consolidate the MarketIcon between this and the other one
	return (
		<TableV2.BodyCell className="uppercase" innerClassName="items-center pr-4">
			<button
				onClick={props?.handleClickMarket}
				className="flex flex-row items-center text-left"
			>
				<MarketIcon
					marketSymbol={props.position.marketSymbol}
					className={'mr-2 h-6 w-6'}
					isSpot={!isPerp}
				/>
				<div>
					<div className="hidden pl-2 normal-case xl:block">{`${
						props?.position?.marketSymbol
					}`}</div>
					<div
						className={cj(
							'pl-2 capitalize',
							props.position.direction === 'long'
								? 'text-text-positive-green-button'
								: 'text-text-negative-red-button'
						)}
					>
						{isPerp
							? props.position.direction === 'long'
								? 'Long'
								: 'Short'
							: props.position.direction === 'long'
								? 'Buy'
								: 'Sell'}
					</div>
				</div>
			</button>
		</TableV2.BodyCell>
	);
};

export const EMPTY_TABLE_VALUE = '-';

const EmptyValueFiller = (props: { card?: boolean }) => (
	<div
		className={`h-4 ${
			props.card ? 'inline-block' : 'flex w-full justify-center'
		}`}
	>
		<span className="relative bottom-0.5">{EMPTY_TABLE_VALUE}</span>
	</div>
);

interface TableCardProps extends CommonTableItemProps {
	uiMarket: UIMarket;
	marketString?: string;
	direction?: PositionDirection;
	actionButtons?: React.ReactNode;
	topRightElement?: React.ReactNode;
	isDataRow?: boolean;
	useSpotWording?: boolean;
	allowNavigateToMarket?: boolean;
	reduceOnlyPerpMarket?: boolean;
}

const TableCard = (props: TableCardProps) => {
	const { switchMarket } = useSwitchMarket();
	const currentPage = useDriftStore((s) => s.currentPageRoute);

	const handleNavigateToMarket = () => {
		if (props.allowNavigateToMarket) {
			switchMarket({
				marketIndex: props.uiMarket.marketIndex,
				marketType: props.uiMarket.marketType,
				goToTradePage: currentPage !== PageRoute.trade, // prevent refreshing the page when already on the trade page
			});
		}
	};

	return (
		<div
			className={cm(
				`mx-4 my-4 rounded-sm bg-container-bg px-2 py-4 text-text-default`,
				props.className
			)}
			data-puppet-tag={props.isDataRow ? 'table_data_row' : undefined}
		>
			<div className="flex w-full flex-row items-center justify-between">
				<div
					className={'inline-flex items-center space-x-2 text-md'}
					onClick={
						props.allowNavigateToMarket ? handleNavigateToMarket : undefined
					}
				>
					<MarketIcon
						className="size-5"
						marketSymbol={props.marketSymbol}
						useNextApiRoute={props.useNextApiRouteForMarketIcon}
					/>

					<span className="line-clamp-1 max-w-[200px] overflow-hidden typo-t4 text-wrap text-ellipsis text-text-default">
						{props.marketString}
					</span>
					<Tag
						className={cj(
							'inline-block w-auto',
							matchEnum(PositionDirection.LONG, props.direction)
								? 'bg-positive-green-secondary-bg text-positive-green'
								: 'bg-negative-red-secondary-bg text-negative-red'
						)}
					>
						{props.direction
							? matchEnum(PositionDirection.LONG, props.direction)
								? props.useSpotWording
									? 'Buy'
									: 'Long'
								: props?.useSpotWording
									? 'Sell'
									: 'Short'
							: ''}
					</Tag>
					{isDelistingTimestampInFuture() &&
						marketToBeDelisted(props.uiMarket.marketIndex) &&
						props.reduceOnlyPerpMarket && (
							<Tooltip content={<span>{getDelistingTextOpenPosition()}</span>}>
								<Info size={18} color={getColorVar('star-yellow')} />
							</Tooltip>
						)}
				</div>
				<div>{props.topRightElement}</div>
			</div>
			<div className="flex flex-row flex-wrap">{props.children}</div>
			<div className="mt-2">{props.actionButtons}</div>
		</div>
	);
};

const CardValue = ({
	value,
	label,
	icon,
	highlight,
	isMobileTopRow,
	className,
	valueClassName,
	onClick,
}: {
	value: ReactNode;
	label: ReactNode;
	icon?: JSX.Element;
	highlight?: boolean;
	isMobileTopRow?: boolean;
	className?: string;
	valueClassName?: string;
	onClick?: () => void;
}) => {
	const memoedClassName = useMemo(
		() =>
			cm(
				'flex flex-col items-start',
				isMobileTopRow ? 'pt-0' : 'pt-2',
				className
			),
		[isMobileTopRow, highlight, className]
	);

	return (
		<div
			className={memoedClassName}
			onClick={() => {
				onClick?.();
			}}
		>
			<div className="flex gap-x-xs">
				<span className="typo-b3 text-text-secondary">{label}</span>
				{icon && icon}
			</div>
			<div className={cj('typo-t3 text-text-default', valueClassName)}>
				{value}
			</div>
		</div>
	);
};

interface PositionWithAccountInfo extends OpenPosition {
	accountName: string;
	accountKey: string;
}

const UnsettledPnlTableCard = (props: {
	key?: string;
	className?: string;
	style?: CSSProperties;
	position: PositionWithAccountInfo;
	onClaim: () => void;
}) => {
	return (
		<div className="flex flex-col border-b border-stroke-divider bg-container-bg p-4 last:rounded-b-sm last:border-none">
			<div className="flex w-full items-start justify-between">
				<div className="flex items-center gap-x-xs">
					<MarketIcon
						marketSymbol={props.position.marketSymbol}
						className="size-5"
					/>
					<span className="line-clamp-2 flex max-w-[200px] items-center overflow-hidden typo-t4 text-wrap text-ellipsis text-text-default">
						{props.position.marketSymbol}
					</span>
				</div>
				<div>
					<Button
						size="sm"
						variant="secondary"
						disabled={props.position.unsettledPnl.eq(ZERO)}
						onClick={props.onClaim}
					>
						Settle
					</Button>
				</div>
			</div>
			<div className="flex items-center justify-between">
				<CardValue
					label="Cost Basis"
					value={
						<NumberDisplay
							type="currency"
							value={BigNum.from(props.position.costBasis, PRICE_PRECISION_EXP)}
							isAccountValueToHide
						/>
					}
				/>
				<CardValue
					label="Settled"
					value={
						<NumberDisplay
							type="currency"
							value={BigNum.from(
								props.position.realizedPnl,
								QUOTE_PRECISION_EXP
							)}
							isAccountValueToHide
							signDependentColor
						/>
					}
				/>
			</div>
			<div className="flex items-center justify-between">
				<CardValue
					label="Unsettled Funding"
					value={
						<NumberDisplay
							className="mr-1 inline-flex"
							type="currency"
							value={BigNum.from(
								props.position.unsettledFundingPnl,
								QUOTE_PRECISION_EXP
							)}
							signDependentColor
							isAccountValueToHide
						/>
					}
				/>
				<CardValue
					label="Claimable / Unsettled"
					className="items-end"
					value={
						<>
							<NumberDisplay
								className="mr-1 inline-flex"
								type="currency"
								value={BigNum.from(
									props.position.unsettledPnl,
									QUOTE_PRECISION_EXP
								)}
								signDependentColor
								isAccountValueToHide
							/>
							<span> / </span>
							<NumberDisplay
								className="ml-1 inline-flex"
								type="currency"
								value={BigNum.from(
									props.position.totalUnrealizedPnl,
									QUOTE_PRECISION_EXP
								)}
								signDependentColor
								isAccountValueToHide
							/>
						</>
					}
				/>
			</div>
		</div>
	);
};

const TableV2 = {
	Skeleton,
	HeaderRow,
	BodyRow,
	SummaryRow,
	BodyRowWrapper,
	HeaderCell,
	TooltipHeaderCell,
	SortableHeaderCell,
	BodyCell,
	MarketCell,
	NotionalCell,
	AssetCell,
	DateCell,
	ChartCell,
	ActionCell,
	OpenPositionMarketDirectionCell,
	EmptyValueFiller,
	UnsettledPnlTableCard,
	TableCard,
	CardValue,
	TradeSizeCell,
};

export default TableV2;
